import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Form, FormError, FormField, FormSchemaEntry, FormType } from '@gik/ui';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';
import { IUserLoginFormResponse, login } from '@gik/api/users/auth';
import ReactDOM from 'react-dom';
import { BadRequest } from '@gik/api/HttpErrors';

const blockName = 'login-form';

const schema: FormSchemaEntry[] = [
  { placeholder: 'Email address', name: 'email', type: 'email', required: true },
  { placeholder: 'Password', name: 'password', type: 'password', required: true },
  {
    name: 'rememberMe',
    type: 'checkbox',
    props: {
      label: 'Remember Me',
    },
  },
];

export interface IUserLoginFormValues {
  email?: string;
  password?: string;
  rememberMe?: boolean;
}

export interface ILoginFormProps {
  buttonsPortal?: () => HTMLElement;
  onSuccess?: (response: IUserLoginFormResponse, values: IUserLoginFormValues) => void;
  onForgotPassword?: () => void;
  onSignup?: () => void;
  onAttemptStart?: () => void;
  onAttemptEnd?: () => void;
  onChange?: (values: object) => void;
  hideEmailField?: boolean;
  hideRememberMe?: boolean;
  hideButtons?: boolean;
  initialValues?: Partial<IUserLoginFormValues>;
  trackingId?: string;
}

export default React.forwardRef<FormType, ILoginFormProps>(function LoginForm(
  {
    buttonsPortal,
    onSuccess,
    onForgotPassword,
    onSignup,
    onChange,
    onAttemptStart,
    onAttemptEnd,
    hideEmailField = false,
    hideRememberMe = false,
    hideButtons = false,
    initialValues,
    trackingId,
  },
  ref
) {
  const bem = bemBlock(blockName);
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const formId = 'loginForm';
  const formRef = React.useRef<FormType>();
  React.useImperativeHandle(ref, () => formRef.current);

  async function handleSubmit(values: IUserLoginFormValues) {
    onAttemptStart?.();
    setErrorMessage(null);
    try {
      const response = await login(values.email, values.password, values.rememberMe);
      const formResponse: IUserLoginFormResponse = {
        ...response,
        rememberMe: values.rememberMe,
      };
      if (onSuccess) {
        onSuccess(formResponse, values);
      }
    } catch (err) {
      switch (true) {
        case err instanceof BadRequest:
          setErrorMessage(err.message);
          break;
        default:
          setErrorMessage(err.message);
      }
    }
    onAttemptEnd?.();
  }

  function handleForgotClick() {
    if (onForgotPassword) {
      onForgotPassword();
    }
  }

  function handleSignup() {
    if (onSignup) {
      onSignup();
    }
  }

  return (
    <Form
      ref={formRef}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onChange={onChange}
      schema={schema}
      vertical
      className={bem()}
      id={formId}
      trackingId={trackingId}
      render={({ submitting, valid }) => {
        const buttons = (
          <>
            <Button
              className={bem('btnSubmit')}
              variant="primary"
              type="submit"
              size="lg"
              loading={submitting}
              disabled={!valid}
              form={formId}
              fullWidth
            >
              {t(translationKeys.formSubmitButton)}
            </Button>
          </>
        );

        return (
          <>
            {errorMessage && <FormError message={errorMessage} centered />}
            <fieldset>
              {!hideEmailField && <FormField name="email" />}
              <FormField name="password" />
              <section>
                <Button variant="primary-link" onClick={handleForgotClick}>
                  {t(translationKeys.formForgotPassword)}
                </Button>
                {!hideRememberMe && <div className="tw-flex-1" />}
                {!hideRememberMe && <FormField name="rememberMe" />}
              </section>

              {!hideButtons && (
                <div className={bem('register')}>
                  {t(translationKeys.formNoAccountSignup)}{' '}
                  <Button onClick={handleSignup} variant="primary-link">
                    {t(translationKeys.formNoAccountSignupLink)}
                  </Button>
                </div>
              )}
            </fieldset>

            {buttonsPortal && !hideButtons && ReactDOM.createPortal(buttons, buttonsPortal())}
            {!buttonsPortal && !hideButtons && buttons}
          </>
        );
      }}
    />
  );
});
